import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Pagination,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  UserCheck,
  UserX,
  Mail,
  Phone,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { teachersApi, classroomsApi, Teacher, Classroom } from '../../services/schoolApi';
import ConfirmDialog from '../../components/ConfirmDialog';

const Teachers = () => {
  const { language } = useLanguage();
  
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [teacherToDelete, setTeacherToDelete] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState<Teacher | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClassroom, setFilterClassroom] = useState('');

  const [formData, setFormData] = useState({
    code: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    classes: [] as string[],
  });

  const text = {
    fr: {
      title: 'Gestion des Enseignants',
      description: 'Liste et gestion de tous les enseignants',
      addTeacher: 'Ajouter un enseignant',
      search: 'Rechercher un enseignant...',
      filterClassroom: 'Filtrer par classe',
      code: 'Code',
      all: 'Tous',
      firstName: 'Prénom',
      lastName: 'Nom',
      phone: 'Téléphone',
      email: 'Email',
      classes: 'Classes',
      actions: 'Actions',
      edit: 'Modifier',
      delete: 'Supprimer',
      active: 'Actif',
      inactive: 'Inactif',
      cancel: 'Annuler',
      save: 'Enregistrer',
      editTeacher: 'Modifier l\'enseignant',
      deleteConfirm: 'Êtes-vous sûr de vouloir supprimer cet enseignant ?',
      noTeachers: 'Aucun enseignant trouvé',
      loading: 'Chargement...',
      status: 'Statut',
    },
    en: {
      title: 'Teachers Management',
      description: 'List and manage all teachers',
      addTeacher: 'Add Teacher',
      search: 'Search teacher...',
      filterClassroom: 'Filter by class',
      code: 'Code',
      all: 'All',
      firstName: 'First Name',
      lastName: 'Last Name',
      phone: 'Phone',
      email: 'Email',
      classes: 'Classes',
      actions: 'Actions',
      edit: 'Edit',
      delete: 'Delete',
      active: 'Active',
      inactive: 'Inactive',
      cancel: 'Cancel',
      save: 'Save',
      editTeacher: 'Edit Teacher',
      deleteConfirm: 'Are you sure you want to delete this teacher?',
      noTeachers: 'No teachers found',
      loading: 'Loading...',
      status: 'Status',
    }
  };

  const currentText = text[language as keyof typeof text];


  useEffect(() => {
    fetchTeachers();
    fetchClassrooms();
  }, [page, searchTerm, filterClassroom]);

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const response = await teachersApi.getAll({
        page,
        limit: 10,
        search: searchTerm || undefined,
        classroomId: filterClassroom || undefined,
      });
      setTeachers(response.data);
      setTotalPages(response.totalPages);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des enseignants');
    } finally {
      setLoading(false);
    }
  };

  const fetchClassrooms = async () => {
    try {
      const response = await classroomsApi.getAll({ limit: 100 });
      setClassrooms(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des classes:', err);
    }
  };

  const handleOpenDialog = (teacher?: Teacher) => {
    if (teacher) {
      setEditingTeacher(teacher);
      setFormData({
        code: teacher.code || '',
        firstName: teacher.firstName || '',
        lastName: teacher.lastName,
        phone: teacher.phone || '',
        email: teacher.email || '',
        classes: teacher.classes?.map(c => typeof c === 'string' ? c : c._id) || [],
      });
    } else {
      setEditingTeacher(null);
      setFormData({
        code: '',
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        classes: [],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTeacher(null);
  };

  const handleSubmit = async () => {
    try {
      const data = formData;
      
      if (editingTeacher) {
        await teachersApi.update(editingTeacher._id, data);
      } else {
        await teachersApi.create(data);
      }
      handleCloseDialog();
      fetchTeachers();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDeleteClick = (id: string) => {
    setTeacherToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (teacherToDelete) {
      try {
        await teachersApi.delete(teacherToDelete);
        setDeleteDialogOpen(false);
        setTeacherToDelete(null);
        fetchTeachers();
      } catch (err: any) {
        setError(err.message || 'Erreur lors de la suppression');
        setDeleteDialogOpen(false);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTeacherToDelete(null);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#0D3C4C', mb: 1 }}>
            {currentText.title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {currentText.description}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Plus size={20} />}
          onClick={() => handleOpenDialog()}
          sx={{ 
            backgroundColor: '#0D3C4C',
            '&:hover': { backgroundColor: '#1A5F7A' }
          }}
        >
          {currentText.addTeacher}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder={currentText.search}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>{currentText.filterClassroom}</InputLabel>
            <Select
              value={filterClassroom}
              onChange={(e) => setFilterClassroom(e.target.value)}
              label={currentText.filterClassroom}
            >
              <MenuItem value="">{currentText.all}</MenuItem>
              {classrooms.map((classroom) => (
                <MenuItem key={classroom._id} value={classroom._id}>
                  {classroom.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.firstName}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.lastName}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.code}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.phone}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.email}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.status}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.actions}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }}>{currentText.loading}</Typography>
                </TableCell>
              </TableRow>
            ) : teachers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">{currentText.noTeachers}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              teachers.map((teacher) => (
                <TableRow key={teacher._id} hover>
                  <TableCell>{teacher.firstName}</TableCell>
                  <TableCell>{teacher.lastName}</TableCell>
                  <TableCell>{teacher.code || '-'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Phone size={16} />
                      {teacher.phone}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {teacher.email ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Mail size={16} />
                        {teacher.email}
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={teacher.active ? <UserCheck size={16} /> : <UserX size={16} />}
                      label={teacher.active ? currentText.active : currentText.inactive}
                      color={teacher.active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(teacher)}
                      sx={{ color: '#0D3C4C' }}
                    >
                      <Edit size={18} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(teacher._id)}
                      sx={{ color: '#d32f2f' }}
                    >
                      <Trash2 size={18} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTeacher ? currentText.editTeacher : currentText.addTeacher}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
            <TextField
              label={currentText.firstName}
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label={currentText.lastName}
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label={currentText.phone}
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label={currentText.email}
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
            />
            <TextField
              label={currentText.code}
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth sx={{ gridColumn: '1 / -1' }}>
              <InputLabel>{currentText.classes}</InputLabel>
              <Select
                multiple
                value={formData.classes}
                onChange={(e) => setFormData({ ...formData, classes: e.target.value as string[] })}
                label={currentText.classes}
              >
                {classrooms.map((classroom) => (
                  <MenuItem key={classroom._id} value={classroom._id}>
                    {classroom.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{currentText.cancel}</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{ 
              backgroundColor: '#0D3C4C',
              '&:hover': { backgroundColor: '#1A5F7A' }
            }}
          >
            {currentText.save}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title={language === 'fr' ? 'Supprimer l\'enseignant' : 'Delete Teacher'}
        message={language === 'fr' 
          ? 'Êtes-vous sûr de vouloir supprimer cet enseignant ? Cette action est irréversible.'
          : 'Are you sure you want to delete this teacher? This action is irreversible.'}
        confirmText={language === 'fr' ? 'Supprimer' : 'Delete'}
        cancelText={currentText.cancel}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        type="danger"
      />
    </Container>
  );
};

export default Teachers;
